import { useCallback } from 'react'

import { Mission } from '@/features/missions/types/mission'

import { normalize } from './normalize'
import { shouldSendOnce, throttleOncePer } from './shouldSendOnce'
import { useAnalytics } from './useAnalytics'

export function useMissionsAnalytics() {
  const { track } = useAnalytics()

  const trackMissionViewed = useCallback(
    (mission: Mission) => {
      if (shouldSendOnce('mission_viewed', mission.id, 30 * 60 * 1000)) {
        track.mission.viewed({
          ...normalize.mission(mission),
          mission_type: mission.kind,
          recurrence_type: mission.recurrence_kind,
          is_general: mission.isGeneral,
          has_rewards: !!(mission.coin_reward || mission.point_reward),
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  const trackMissionStarted = useCallback(
    (mission: Mission) => {
      if (shouldSendOnce('mission_started', mission.id, 30 * 60 * 1000)) {
        track.mission.started({
          ...normalize.mission(mission),
          mission_type: mission.kind,
          recurrence_type: mission.recurrence_kind,
          is_general: mission.isGeneral,
          evidence_type: mission.evidence_kind,
          requires_confirmation: mission.confirmation_required,
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  const trackMissionCompleted = useCallback(
    (mission: Mission) => {
      if (shouldSendOnce('mission_completed', mission.id, 30 * 60 * 1000)) {
        track.mission.completed({
          ...normalize.mission(mission),
          mission_type: mission.kind,
          recurrence_type: mission.recurrence_kind,
          final_progress: mission._progress?.current_count || 0,
          target_progress: mission._progress?.total_count || 0,
          completion_percentage: mission._progress?.percentage || 100,
          points_reward: mission.point_reward,
          coins_reward: mission.coin_reward,
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  const trackMissionFailed = useCallback(
    (
      mission: Mission,
      reason: 'submission_refused' | 'timeout' | 'unknown'
    ) => {
      track.mission.failed({
        ...normalize.mission(mission),
        failure_reason: reason,
        current_progress: mission._progress?.current_count || 0,
        target_progress: mission._progress?.total_count || 0,
        context_route: window.location.pathname
      })
    },
    [track]
  )

  const trackMissionRewardClaimed = useCallback(
    (mission: Mission) => {
      if (
        shouldSendOnce('mission_reward_claimed', mission.id, 30 * 60 * 1000)
      ) {
        track.mission.rewardClaimed({
          ...normalize.mission(mission),
          points_claimed: mission.point_reward,
          coins_claimed: mission.coin_reward,
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  const trackMissionListInteracted = useCallback(
    (
      interactionType: 'filter' | 'search' | 'sort',
      resultsCount: number,
      filters?: any
    ) => {
      const key = `mission_list:${interactionType}`
      if (throttleOncePer(key, 2000)) {
        track.mission.listInteracted({
          ...normalize.listInteraction(interactionType, resultsCount, filters),
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  return {
    trackMissionViewed,
    trackMissionStarted,
    trackMissionCompleted,
    trackMissionFailed,
    trackMissionRewardClaimed,
    trackMissionListInteracted
  }
}
